import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Mail, MessageCircle, Phone, Sparkles } from "lucide-react";
import {
  STATUS_OPTIONS,
  CONTACT_EMAIL,
  WHATSAPP_DISPLAY,
  WHATSAPP_URL,
  WHATSAPP_NUMBER,
} from "@/lib/site-data";

export default function ContactPage() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    current_status: "Student",
    target_role: "",
    message: "",
  });
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (!form.full_name || !form.email || !form.phone || !form.target_role) {
      setError("Please fill in all required fields.");
      return;
    }
    const text = [
      "Hi Xyro Tech, I'd like to book a free career consultation.",
      "",
      `Name: ${form.full_name}`,
      `Email: ${form.email}`,
      `Phone: ${form.phone}`,
      `Current Status: ${form.current_status}`,
      `Target Role: ${form.target_role}`,
      form.message ? `Message: ${form.message}` : null,
    ].filter(Boolean).join("\n");
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setDone(true);
  };

  return (
    <main className="relative pt-32 sm:pt-36 pb-24 sm:pb-32 overflow-hidden" data-testid="contact-page">
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-20 -left-32 w-[500px] h-[500px] rounded-full opacity-60"
          style={{ background: "radial-gradient(circle, rgba(43,77,255,0.4), transparent 70%)", filter: "blur(120px)" }}
        />
        <div
          className="absolute top-60 -right-32 w-[500px] h-[500px] rounded-full opacity-50"
          style={{ background: "radial-gradient(circle, rgba(138,43,226,0.4), transparent 70%)", filter: "blur(120px)" }}
        />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-xs text-zinc-300">
            <Sparkles className="w-3.5 h-3.5 text-[#5b78ff]" /> 30-min free consultation
          </div>
          <h1 className="mt-6 font-heading text-5xl md:text-6xl font-bold text-white tracking-tighter leading-[1.05]">
            Let&apos;s plan your <span className="text-gradient-electric">US career.</span>
          </h1>
          <p className="mt-5 text-zinc-400 text-lg leading-relaxed max-w-xl">
            Share a few details and a US career strategist will reach out within 24 hours to build your personalized acceleration plan.
          </p>

          <div className="mt-10 space-y-4">
            <a href={`mailto:${CONTACT_EMAIL}`} className="glass p-5 flex items-center gap-4 hover:border-white/20 transition-all">
              <div className="w-11 h-11 rounded-xl bg-[#2B4DFF]/15 border border-[#2B4DFF]/30 flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#9db1ff]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-zinc-500">Email</p>
                <p className="text-white">{CONTACT_EMAIL}</p>
              </div>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="glass p-5 flex items-center gap-4 hover:border-white/20 transition-all">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-emerald-300" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-zinc-500">WhatsApp · Fastest response</p>
                <p className="text-white">{WHATSAPP_DISPLAY}</p>
              </div>
            </a>
            <div className="glass p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center">
                <Phone className="w-5 h-5 text-zinc-300" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-zinc-500">Phone</p>
                <p className="text-white">{WHATSAPP_DISPLAY}</p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="glass-prism p-7 md:p-10"
        >
          {!done ? (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <h2 className="font-heading text-2xl text-white mb-2">Book My Free Consultation</h2>
              <p className="text-sm text-zinc-400 mb-6">No obligations. No spam. Just a plan.</p>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name" required>
                  <input
                    type="text" required value={form.full_name} onChange={onChange("full_name")}
                    data-testid="contact-input-name"
                    className="input-xyro"
                    placeholder="Jane Doe"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email" required value={form.email} onChange={onChange("email")}
                    data-testid="contact-input-email"
                    className="input-xyro"
                    placeholder="you@email.com"
                  />
                </Field>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Phone number" required>
                  <input
                    type="tel" required value={form.phone} onChange={onChange("phone")}
                    data-testid="contact-input-phone"
                    className="input-xyro"
                    placeholder="+1 555 000 0000"
                  />
                </Field>
                <Field label="Current status" required>
                  <select
                    required value={form.current_status} onChange={onChange("current_status")}
                    data-testid="contact-input-status"
                    className="input-xyro"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s} className="bg-black">{s}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Target role" required>
                <input
                  type="text" required value={form.target_role} onChange={onChange("target_role")}
                  data-testid="contact-input-role"
                  className="input-xyro"
                  placeholder="Data Scientist, AI Engineer, Cloud Engineer..."
                />
              </Field>

              <Field label="Message (optional)">
                <textarea
                  rows={4} value={form.message} onChange={onChange("message")}
                  data-testid="contact-input-message"
                  className="input-xyro resize-none"
                  placeholder="Tell us about your goals, timeline and visa situation."
                />
              </Field>

              {error && <p className="text-sm text-red-400" data-testid="contact-error">{error}</p>}

              <button
                type="submit"
                data-testid="contact-submit"
                className="btn-primary-xyro inline-flex w-full justify-center items-center gap-2 rounded-full px-6 py-4 font-medium"
              >
                <MessageCircle className="w-4 h-4" /> Send via WhatsApp <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-xs text-zinc-500 text-center pt-1">
                Clicking submit opens WhatsApp with your details prefilled — fastest response.
              </p>
            </form>
          ) : (
            <div className="py-10 text-center" data-testid="contact-success">
              <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center mb-5">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="font-heading text-3xl text-white">Almost there.</h2>
              <p className="mt-3 text-zinc-400 max-w-sm mx-auto">
                We&apos;ve opened WhatsApp with your details prefilled. Just hit send and we&apos;ll respond within minutes.
              </p>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-6 btn-primary-xyro inline-flex items-center gap-2 rounded-full px-6 py-3 font-medium"
              >
                <MessageCircle className="w-4 h-4" /> Open WhatsApp again
              </a>
            </div>
          )}
        </motion.div>
      </div>

      <style>{`
        .input-xyro {
          width: 100%;
          padding: 0.85rem 1rem;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          color: #fff;
          border-radius: 0.75rem;
          font-size: 0.95rem;
          transition: border-color 0.2s, background 0.2s;
        }
        .input-xyro::placeholder { color: rgba(161,161,170,0.6); }
        .input-xyro:focus {
          outline: none;
          border-color: rgba(43,77,255,0.55);
          background: rgba(255,255,255,0.06);
        }
      `}</style>
    </main>
  );
}

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.18em] text-zinc-400 mb-2">
      {label} {required && <span className="text-[#5b78ff]">*</span>}
    </span>
    {children}
  </label>
);
